/* eslint-disable no-alert */
import { FormikErrors } from 'formik'
import { filter, length, map, pipe } from 'ramda'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { DatasetSelectField } from '../../components/DatasetSelect'
import ObservationsSelectField from '../../components/ObservationsSelectField/ObservationsSelectField'
import { USER_LEVEL } from '../../components/Settings/definitions'
import { SettingsContext } from '../../components/Settings/SettingsContext'
import TrainingInfo from '../../components/TrainingInfo/TrainingInfo'
import { useStore } from '../../store/store'
import { createTraining, Metadata } from '../../store/storeActions'
import { SelectOption } from '../../types'
import { BackboneSelectOption } from '../../types/backbone'
import { ObservationCompact } from '../../types/observation'
import AnomalyStrategySelect from './AnomalyStrategySelect/AnomalyStrategySelect'
import BackboneSelect from './BackboneSelect/BackboneSelect'
import { MAX_TRAINING_IMAGES } from './const'
import FP16Switch from './FP16Switch'
import ResolutionInputRange from './ResolutionSelect/ResolutionInputRange'
import ResolutionSelect from './ResolutionSelect/ResolutionSelect'
import useStyles from './ResolutionSelect/ResolutionSelect.styles'
import TrainingNewForm, { TrainingNewFormValues } from './TrainingNewForm'
import {
  getEstimatedTrainingsDuration,
  getResolutionsErrorMessage,
  ITrainingDuration,
} from './utils'

export type AnomalyFormValues = TrainingNewFormValues & {
  anomalyStrategy?: SelectOption
}

interface RouteParams {
  projectId: string
  productId: string
}

const requiredImages = 5

const NORMAL_CLASS = 'normal'

const NewAnomalyTraining = () => {
  const { t } = useTranslation()
  const { product } = useStore()
  const history = useHistory()
  const { productId, projectId } = useParams<RouteParams>()
  const { settings } = useContext(SettingsContext)
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])
  const resolutionDftValue = { value: 'standard', label: 'Standard' }
  const [selectedResolution, setSelectedResolution] = useState<
    SelectOption | undefined
  >(resolutionDftValue)
  const [selectedBackbone, setSelectedBackbone] = useState<
    BackboneSelectOption[] | undefined
  >()
  const MAX_RESOLUTION = 200704
  const DFT_RES = 224
  const STEP = 1
  const [customHeight, setCustomHeight] = useState(DFT_RES)
  const [customWidth, setCustomWidth] = useState(DFT_RES)
  const [estimatedTrainingsDuration, setEstimatedTrainingsDuration] = useState<
    ITrainingDuration[]
  >([])
  const [
    hasNonAnnotatedImagesInDataset,
    setHasNonAnnotatedImagesInDataset,
  ] = useState(false)

  const styles = useStyles()

  function isBackboneAndStrategyCompatible(values: AnomalyFormValues) {
    let isCompatible = true
    if (
      values.anomalyStrategy?.label === 'Simple' &&
      values.backbone !== undefined
    ) {
      const backbones: SelectOption[] = (values.backbone as unknown) as SelectOption[]
      backbones.forEach((v: SelectOption) => {
        if (v.label === 'Heavy') {
          isCompatible = false
        }
      })
    }
    return isCompatible
  }

  return product ? (
    <TrainingNewForm<AnomalyFormValues>
      productType={product.type}
      productId={productId}
      requiredImages={requiredImages}
      initialValues={{
        selectedObservations: [],
        name: '',
        notes: '',
        startTest: false,
        fp16: true,
      }}
      renderRightColumn={({ values, setFieldValue }) => (
        <>
          <TrainingInfo
            selectedObservations={values.selectedObservations}
            productType={product.type}
            // showOnlyTotal={isSegmentationProduct(productType)}
            showOnlyTotal={false}
          />

          {settings.preference__user_level === USER_LEVEL.expert && (
            <DatasetSelectField
              fieldName="dataset"
              productId={Number(productId)}
              onSelectedDatasets={setSelectedDatasets}
            />
          )}

          <BackboneSelect
            fieldName="backbone"
            productId={product.id}
            onSelectedBackbone={setSelectedBackbone}
            onSelectedResolution={setSelectedResolution}
          />

          <FP16Switch fieldName="fp16" />

          <ResolutionSelect
            fieldName="resolution"
            productId={product.id}
            disabled={
              values.backbone === undefined ||
              selectedBackbone?.length === 0 ||
              settings.preference__user_level === USER_LEVEL.basic
            }
            dftValue={selectedResolution}
            onSelectedResolution={setSelectedResolution}
            setCustomHeight={setCustomHeight}
            setCustomWidth={setCustomWidth}
            backboneValues={selectedBackbone}
            producType={product.type}
          />

          <div
            className={
              selectedResolution?.value === 'custom'
                ? styles.visible
                : styles.hidden
            }
          >
            <ResolutionInputRange
              customHeight={customHeight}
              customWidth={customWidth}
              setCustomHeight={setCustomHeight}
              setCustomWidth={setCustomWidth}
              getResolutionsErrorMessage={getResolutionsErrorMessage}
              maxResolution={MAX_RESOLUTION}
              step={STEP}
              productType={product.type}
              t={t}
              onResolutionInputRangeChange={(newHeight, newWidth) => {
                setEstimatedTrainingsDuration(
                  getEstimatedTrainingsDuration({
                    productType: product.type,
                    numOfImages: values.selectedObservations.length,
                    strategy: values.anomalyStrategy?.label,
                    resolution: {
                      height: newHeight,
                      width: newWidth,
                    },
                    backbones: values.backbone?.map((b) => ({
                      code: b.code,
                      label: b.label,
                    })),
                    precision: values.fp16 ? 'fp16' : 'fp32',
                  })
                )
              }}
            />
          </div>
          <AnomalyStrategySelect
            fieldName="anomalyStrategy"
            selectedBackbone={selectedBackbone}
          />
        </>
      )}
      validate={(values) => {
        const errors: FormikErrors<{ [field: string]: string }> = {}

        values.backbone?.forEach((b) => {
          if (b.code === 'smartcam_anomaly' && values.fp16) {
            errors.fp16 = t(
              'The {{ precision }} model precision and the selected backbone are not compatible. Please change selection.',
              { precision: 'FP16' }
            )
          }
        })

        const resolutionsError = getResolutionsErrorMessage(
          customHeight,
          customWidth,
          MAX_RESOLUTION,
          product.type,
          t
        )

        if (
          values.selectedObservations.some((o) => o.productClasses.length === 0)
        ) {
          errors.selectedObservations = t('Cannot use non-annotated images')
        }

        const normalObservationImages = pipe(
          map((o: ObservationCompact) => o.productClasses),
          map((classes: string[]) => classes.join(', ')),
          filter((tag: string) => tag.includes(NORMAL_CLASS)),
          length
        )(values.selectedObservations)

        const len = values.selectedObservations.length
        let maxImages = MAX_TRAINING_IMAGES

        // limit the number or selected images to avoid pipeline failures (oom errors)
        if (selectedBackbone && selectedBackbone.length > 0) {
          if (selectedBackbone?.some((b) => b.name === 'Heavy')) {
            maxImages = values.fp16 ? 800 : 400
          } else {
            maxImages = values.fp16 ? 1200 : 600
          }
        }

        if (len < requiredImages) {
          errors.selectedObservations = t(
            'You need to use at least {{ count }} image',
            { count: requiredImages }
          )
        } else if (len > maxImages) {
          errors.selectedObservations = t(
            'To avoid long training times, you can select up to {{ count }} images',
            { count: maxImages }
          )
        }

        if (resolutionsError !== '') {
          errors.selectedObservations = resolutionsError
        }

        if (normalObservationImages < requiredImages) {
          errors.selectedObservations = t(
            'You need to use at least {{ count }} image annotated as normal',
            { count: requiredImages }
          )
        }

        if (!values.backbone || values.backbone.length === 0) {
          errors.backbone = t('This field is required')
        }

        if (!values.anomalyStrategy) {
          errors.anomalyStrategy = t('This field is required')
        }

        if (!isBackboneAndStrategyCompatible(values)) {
          errors.anomalyStrategy = t(
            'Selected Backbone and Anomaly Strategy values are not compatible. Please change selection.'
          )
        }
        if (!values.name) {
          errors.name = t('This field is required')
        }

        if (
          values.startTest &&
          values.selectedObservations.length === product.observationsNumber
        ) {
          errors.startTest = t(
            'Deselect at least one image if you want to start a test with unselected images. Otherwise, deselect this option.'
          )
        }

        const trainingDurations = getEstimatedTrainingsDuration({
          productType: product.type,
          numOfImages: values.selectedObservations.length,
          strategy: values.anomalyStrategy?.label,
          resolution: {
            height: customHeight,
            width: customWidth,
          },
          backbones: values.backbone?.map((b) => ({
            code: b.code,
            label: b.label,
          })),
          precision: values.fp16 ? 'fp16' : 'fp32',
        })

        if (Object.keys(errors).length === 0) {
          setEstimatedTrainingsDuration(trainingDurations)
        }

        if (
          trainingDurations.find((d) => d.sec > 240 * 60) // 4 hours
        ) {
          errors.selectedObservations = t(
            'The estimated training time is too long. Please select fewer images or lower the resolution (if possible).'
          )
        }

        return errors
      }}
      estimatedTrainingsDuration={estimatedTrainingsDuration}
      hasNonAnnotatedImagesInDataset={hasNonAnnotatedImagesInDataset}
      onSubmit={(values, setSubmitting) => {
        if (
          values.backbone !== undefined &&
          values.anomalyStrategy !== undefined
        ) {
          const backbones: BackboneSelectOption[] = (values.backbone as unknown) as BackboneSelectOption[]
          backbones.forEach((b: BackboneSelectOption) => {
            let metadata: Metadata = {
              anomalyStrategyId:
                values.anomalyStrategy &&
                b.metadata.trainingAnomalyStrategySupported !== false
                  ? Number(values.anomalyStrategy.value)
                  : undefined,
              inputHeight:
                b.metadata.trainingResolutionSupported !== false &&
                customHeight !== 224
                  ? customHeight
                  : undefined,
              inputWidth:
                b.metadata.trainingResolutionSupported !== false &&
                customWidth !== 224
                  ? customWidth
                  : undefined,
            }
            if (values.fp16) metadata = { ...metadata, precision: 'fp16' }
            createTraining(
              Number(projectId),
              Number(productId),
              Number(b.id),
              values.selectedObservations.map((o) => o.id),
              setSubmitting,
              history.push,
              metadata,
              values.name,
              values.notes,
              values.startTest,
              selectedDatasets.length > 0
                ? selectedDatasets.map((id) => Number(id))
                : undefined
            )
          })
        }
      }}
    >
      {/* overrides the original ObservationSelectField to exclude "anomaly" samples */}
      <ObservationsSelectField
        name="selectedObservations"
        productType={product.type}
        productId={Number(productId)}
        defaultFilters={{ productClasses: [NORMAL_CLASS] }}
        dataset={selectedDatasets.length > 0 ? selectedDatasets : undefined}
        inTraining
        inTest={false}
        onNonAnnotatedImagesInDataset={setHasNonAnnotatedImagesInDataset}
      />
    </TrainingNewForm>
  ) : null
}

export default NewAnomalyTraining
